import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiSearch,
  FiX,
  FiCheckCircle,
  FiDownload,
  FiFileText,
} from "react-icons/fi";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TopInfoBar from "../components/TopInfoBar";
import { authHeader } from "../utils/auth";
import ErrorMessage from "../components/ErrorMessage";
import Loading from "../components/Loading";
import { API_BASE } from "../config";
import { sanitizeString } from "../utils/inputValidation";
import { getUserFriendlyError } from "../utils/errorMessages";

/* ====================== MAIN PAGE ====================== */
export default function CheckLicense() {
  const blue = "#3366CC";
  const text = "#0F172A";

  const isLoggedIn =
    typeof window !== "undefined" && sessionStorage.getItem("isLoggedIn") === "true";

  const container = {
    width: "100%",
    maxWidth: "100%",
    margin: "0 auto",
    background: "#fff",
    boxShadow: "0 8px 28px rgba(16,24,40,.08)",
    overflow: "visible",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
  };

  const responsiveCSS = `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; overflow-x: hidden; width: 100%; }

  .page-container { 
    width: 100% !important; 
    margin: 0 auto; 
    overflow-x: hidden; 
    background: #fff;
  }

  /* คลาสใหม่สำหรับคุมเนื้อหาให้อยู่กลางจอพอดีสายตา */
  .inner-content {
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
    padding: 0 24px;
  }

  @media (max-width: 768px) {
    .top-info-bar { display: none !important; }
    .desktop-nav { display: none !important; }
    .hamburger-btn { display: inline-flex !important; }
    .sticky-nav-inner { height: 60px !important; padding: 0 16px !important; }
    
    .grid-2 { grid-template-columns: 1fr !important; gap: 24px !important; }
    .search-row-layout { grid-template-columns: 1fr !important; }
    .search-btn-responsive { width: 100% !important; }
    .result-grid-responsive { grid-template-columns: 1fr !important; gap: 0 !important; }
    .inner-content { padding: 0 16px; }
    .result-grid-responsive {
      grid-template-columns: 1fr !important;
      padding: 16px !important;
      gap: 0 !important;
    }
    .row-item {
      grid-template-columns: 1fr !important;
      gap: 4px !important;
      padding: 12px 0 !important;
    }
    .action-btn-container {
      justify-content: center !important;
      padding: 16px 16px 24px !important;
    }
  }
  [id] { scroll-margin-top: 70px; }
`;


  return (
    <>
      <style>{responsiveCSS}</style>
      <div style={container}>
        <div style={{ position: "sticky", top: 0, zIndex: 1000, background: "#fff" }}>
          <TopInfoBar blue={blue} />
          <Navbar text={text} isLoggedIn={isLoggedIn} />
        </div>

        <VerifyPane />

        <Footer />
      </div>
    </>
  );
}

/* ================== VERIFY PANE ================== */
function VerifyPane() {
  const navigate = useNavigate();
  
  // Import API_BASE from config
  // API_BASE is already imported at the top of the file

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(null);
  const [error, setError] = useState("");
  const [me, setMe] = useState(null); // info user (org/email) จาก /api/me
  const inputRef = useRef(null);

  // ใช้ authHeader จาก utils/auth.js

  // รับคีย์จากหน้าอื่น (ใช้ sessionStorage เพื่อความปลอดภัย)
  useEffect(() => {
    const cached = sessionStorage.getItem("pendingLicenseQuery") || "";
    if (cached) {
      setQuery(cached);
      handleLookup(cached);
      // ลบหลังจากใช้แล้ว
      sessionStorage.removeItem("pendingLicenseQuery");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const daysRemaining = useMemo(() => {
    if (!record || !record.expiryDate) return null;
    const now = new Date().setHours(0, 0, 0, 0);
    const end = new Date(record.expiryDate).setHours(0, 0, 0, 0);
    return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  }, [record]);

  const mapLicense = (lic, meObj) => {
    // lic: server fields (snake_case)
    const fmt = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "-");
    const remain = lic.expires_at
      ? Math.ceil(
          (new Date(lic.expires_at).setHours(0, 0, 0, 0) -
            new Date().setHours(0, 0, 0, 0)) /
            (1000 * 60 * 60 * 24)
        )
      : null;

    const computedStatus =
      lic.expires_at && remain !== null && remain <= 0
        ? "Expired"
        : (lic.status || "").toLowerCase() === "inactive"
        ? "Expired"
        : "Active";

    const typeLabel = (() => {
      const t = (lic.term || "").toLowerCase();
      if (t === "trial") return "Trial";
      if (t === "subscription") return "Subscription";
      if (t === "perpetual") return "Perpetual";
      return lic.term || "-";
    })();

    return {
      key: lic.license_key,
      status: computedStatus,
      issueDate: fmt(lic.issued_at),
      expiryDate: fmt(lic.expires_at),
      productName:
        lic.product_sku && lic.product_sku !== "-" ? lic.product_sku : "Smart Audit",
      devicesLinked:
        typeof lic.activations_used === "number" &&
        typeof lic.max_activations === "number"
          ? `${lic.activations_used}/${lic.max_activations} Devices`
          : "-",
      lastActivation: "-", // ยังไม่มีใน backend
      activationStatus:
        computedStatus === "Active" ? "Not Activated on this device" : computedStatus,
      notes: "-",
      licenseSource: typeLabel === "Trial" ? "Trial" : "Direct Purchase",
      licenseType: typeLabel,
      client: meObj?.company || "-",
      email: meObj?.email || "-",
    };
  };

  const fetchMe = async () => {
    const res = await fetch(`${API_BASE}/me`, {
      headers: { "Content-Type": "application/json", ...authHeader() },
    });
    if (res.status === 401) {
      // หมดอายุ/ไม่ได้ล็อกอิน
      throw new Error("unauthorized");
    }
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j?.detail || "Failed to load user data.");
    }
    return res.json();
  };

  const fetchLicensesOfClient = async (clientId) => {
    const res = await fetch(
      `${API_BASE}/licenses?clientId=${encodeURIComponent(clientId)}`,
      {
        headers: { "Content-Type": "application/json", ...authHeader() },
      }
    );
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j?.detail || "Failed to load licenses.");
    }
    return res.json();
  };

  const handleLookup = async (val) => {
    const key = sanitizeString((val ?? query).trim());
    setError("");
    setRecord(null);

    if (!key) {
      inputRef.current?.focus();
      return;
    }

    // ต้องมี token
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      setError(getUserFriendlyError("No authentication token", "general"));
      return;
    }

    setLoading(true);
    try {
      // 1) me
      const meObj = me || (await fetchMe());
      if (!me) setMe(meObj);

      // 2) licenses ของ client นี้
      const list = await fetchLicensesOfClient(meObj.id);

      // 3) หาโดย license key (เทียบตรงๆ, ไม่สนตัวเล็กใหญ่เล็กน้อย)
      const found = (list || []).find(
        (x) => (x.license_key || "").toUpperCase() === key.toUpperCase()
      );

      if (!found) {
        // optional: ถ้าพิมพ์ชื่อบริษัท/อีเมลเป็น query แล้วอยากให้โชว์ “ล่าสุด”
        if (
          key.toUpperCase() === (meObj.company || "").toUpperCase() ||
          key.toUpperCase() === (meObj.email || "").toUpperCase()
        ) {
          const latest = (list || [])[0]; // BE คืน id.desc อยู่แล้ว
          if (latest) {
            setRecord(mapLicense(latest, meObj));
            return;
          }
        }
        setError(getUserFriendlyError("License not found", "general"));
        return;
      }

      setRecord(mapLicense(found, meObj));
    } catch (e) {
      if (e?.message === "unauthorized") {
        sessionStorage.removeItem("isLoggedIn");
        sessionStorage.removeItem("access_token");
        sessionStorage.removeItem("token_type");
        sessionStorage.removeItem("postLoginRedirect");
        sessionStorage.removeItem("selectedLicenseKey");
        sessionStorage.removeItem("pendingLicenseQuery");
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("access_token");
        localStorage.removeItem("token_type");
        setError(getUserFriendlyError("unauthorized", "general"));
        setTimeout(() => navigate("/login"), 500);
      } else {
        setError(getUserFriendlyError(e, "general"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      style={{
        background: "#F3F6FB",
        padding: "60px 24px 150px",
        borderTop: "1px solid #E5E7EB",
        borderBottom: "1px solid #E5E7EB",
      }}
    >
      <div style={{ maxWidth: 1120, margin: "0 auto" }}>
        <h1
          style={{
            textAlign: "center",
            color: "#1F376C",
            fontWeight: 800,
            fontSize: 36,
            margin: 0,
          }}
        >
          Verify Your License
        </h1>
        <p
          style={{
            textAlign: "center",
            color: "#334155",
            marginTop: 12,
            marginBottom: 28,
            fontSize: 16,
          }}
        >
          Enter your license key to check status and validity.
        </p>

        {/* Search row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 16,
            alignItems: "center",
            maxWidth: 720,
            margin: "0 auto",
            width: "100%",
          }}
        >
          <div style={{ position: "relative", width: "100%" }}>
            <FiSearch
              style={{
                position: "absolute",
                left: 14,
                top: "50%",
                transform: "translateY(-50%)",
                width: 18,
                height: 18,
                color: "#64748B",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLookup()}
              placeholder="License Key *"
              maxLength={200}
              style={{
                width: "100%",
                height: 48,
                padding: "0 40px 0 38px",
                borderRadius: 10,
                border: "1px solid #CBD5E1",
                background: "#fff",
                fontSize: 16,
                outline: "none",
                boxShadow: "inset 0 1px 2px rgba(0,0,0,.03)",
                boxSizing: "border-box",
              }}
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setRecord(null);
                  setError("");
                  inputRef.current?.focus();
                }}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 24,
                  height: 24,
                  borderRadius: 999,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "#94A3B8",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FiX size={18} />
              </button>
            )}
          </div>

          <button
            onClick={() => handleLookup()}
            disabled={loading}
            aria-label={loading ? "Checking license, please wait" : "Check license"}
            style={{
              height: 48,
              padding: "0 28px",
              borderRadius: 10,
              border: "1px solid #0B80B8",
              background: "#0EA5E9",
              color: "#fff",
              fontWeight: 700,
              fontSize: 16,
              cursor: loading ? "not-allowed" : "pointer",
              boxShadow: "0 4px 12px rgba(14, 165, 233, 0.3)",
              transition: "all 0.2s ease, opacity 0.2s ease",
              opacity: loading ? 0.7 : 1,
            }}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.background = "#0284C7")}
            onMouseLeave={(e) => !loading && (e.currentTarget.style.background = "#0EA5E9")}
          >
            {loading ? "Checking..." : "Check"}
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div style={{ margin: "20px auto 0", maxWidth: 720 }}>
            <ErrorMessage message={error} />
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div style={{ margin: "20px auto 0", maxWidth: 720 }}>
            <Loading message="Checking license..." />
          </div>
        )}

        {/* Result card */}
        {record ? (
          <div
            style={{
              margin: "28px auto 0",
              maxWidth: 1020,
              borderRadius: 12,
              background: "#fff",
              boxShadow: "0 8px 28px rgba(16,24,40,.12)",
              border: "1px solid #E5E7EB",
            }}
          >
            <div style={{ padding: "22px 32px 0", fontSize: 14, color: "#64748B" }}>
              Key: <b style={{ color: "#0F172A" }}>{record.key}</b>
            </div>

            <div className="result-grid-responsive"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 24,
                padding: "12px 32px 8px",
              }}
            >
              <div>
                <Row
                  label="License Status"
                  value={
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        color:
                          record.status === "Active"
                            ? "#16A34A"
                            : record.status === "Expired"
                            ? "#DC2626"
                            : "#D97706",
                        fontWeight: 700,
                      }}
                    >
                      <FiCheckCircle /> {record.status}
                    </span>
                  }
                />
                <Row label="Issue Date" value={fmtDate(record.issueDate)} />
                <Row label="Expiration Date" value={fmtDate(record.expiryDate)} />
                <Row
                  label="Remaining Days"
                  value={
                    daysRemaining == null
                      ? "-"
                      : daysRemaining > 0
                      ? `${daysRemaining} days remaining`
                      : "Expired"
                  }
                />
                <Row label="Product Name" value={record.productName} />
                <Row label="Devices Linked" value={record.devicesLinked} />
                <Row
                  label="Last Activation Date"
                  value={fmtDate(record.lastActivation)}
                />
              </div>

              <div>
                <Row label="Activation Status" value={record.activationStatus} />
                <Row label="Notes / Internal Ref." value={record.notes} />
                <Row label="License Source" value={record.licenseSource} />
                <Row label="License Type" value={record.licenseType} />
                <Row label="Client / Organization" value={record.client} />
                <Row
                  label="Registered Email"
                  value={
                    <a
                      href={`mailto:${record.email}`}
                      style={{ color: "#0EA5E9", textDecoration: "underline" }}
                    >
                      {record.email}
                    </a>
                  }
                />
              </div>
            </div>

            <div className="action-btn-container"
              style={{ 
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 32px 28px",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <a href="/license-sample.pdf" download style={actionBtn("secondary")}>
                  <FiDownload /> Download PDF
                </a>
                <a href="/terms" style={actionBtn("secondary")}>
                  <FiFileText /> View Terms
                </a>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/* ========= Helper Functions ========= */
function Row({ label, value }) {
  return (
    <div style={{ 
      display: "flex", 
      justifyContent: "space-between", 
      padding: "10px 0", 
      borderBottom: "1px inset #f1f5f9",
      gap: "10px"
    }}>
      <span style={{ color: "#64748B", fontSize: 13, flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 600, fontSize: 13, textAlign: "right", wordBreak: "break-word" }}>{value}</span>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso || iso === "-") return "-";
  try {
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return iso;
  }
}

function actionBtn(variant) {
  const base = {
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    border: "1px solid #CBD5E1",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 700,
    cursor: "pointer",
    textDecoration: "none",
  };
  if (variant === "primary") {
    return {
      ...base,
      background: "#0C67B1",
      color: "#fff",
      border: "1px solid #0B5C9E",
    };
  }
  if (variant === "ghost") {
    return {
      ...base,
      background: "transparent",
      color: "#2563EB",
      border: "none",
      padding: 0,
    };
  }
  return {
    ...base,
    background: "#E6F2FB",
    color: "#0B66A7",
    border: "1px solid #CFE8FA",
  };
}

